import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';

import { isLandingHost } from './config/host';

import Login from './pages/Login';
import Layout from './components/Layout';
import Landing from './pages/Landing';
import CargandoGlobal from './components/CargandoGlobal';

const Songs      = React.lazy(() => import('./pages/Songs'));
const SongDetail = React.lazy(() => import('./pages/SongDetail'));
const SongEdit   = React.lazy(() => import('./pages/SongEdit'));
const Setlists      = React.lazy(() => import('./pages/Setlists'));
const SetlistDetail = React.lazy(() => import('./pages/SetlistDetail'));
const SetlistEdit   = React.lazy(() => import('./pages/SetlistEdit'));
const MyAccount     = React.lazy(() => import('./pages/MyAccount'));
const Users      = React.lazy(() => import('./pages/Users'));
const Compartir  = React.lazy(() => import('./pages/Compartir'));
const Categorias = React.lazy(() => import('./pages/Categorias'));
const CancioneroPublico = React.lazy(() => import('./pages/publico/CancioneroPublico'));
const CancionPublica    = React.lazy(() => import('./pages/publico/CancionPublica'));
const Demo              = React.lazy(() => import('./pages/publico/Demo'));
const SuperAdmin = React.lazy(() => import('./pages/SuperAdmin'));

const Loading: React.FC = () => (
  <div className="text-center p-5 fs-5 text-muted">Cargando…</div>
);

const lazy = (element: React.ReactNode) => (
  <React.Suspense fallback={<Loading />}>{element}</React.Suspense>
);

/**
 * Las rutas del cancionero público. Se montan en los DOS modos a propósito:
 * en producción viven en el apex (miensamble.com/c/ibc) y en local hay que
 * poder abrirlas igual. Así el enlace que el panel muestra para copiar es el
 * mismo que funciona aquí, sin una forma distinta por entorno.
 *
 * El prefijo /c/ evita que un grupo llamado "contacto" o "ayuda" choque con
 * una página futura del sitio.
 */
const rutasPublicas = (
  <>
    <Route path="/c/:slug" element={lazy(<CancioneroPublico />)} />
    <Route path="/c/:slug/:id" element={lazy(<CancionPublica />)} />
    {/* /demo es la página que enlaza el botón del APK: sin precios ni pagos */}
    <Route path="/demo" element={lazy(<Demo />)} />
  </>
);

/** miensamble.com y www → sitio público y cancioneros compartidos. */
const LandingApp: React.FC = () => (
  <Router>
    <CargandoGlobal />
    <Routes>
      <Route path="/" element={<Landing />} />
      {rutasPublicas}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Router>
);

const App: React.FC = () => {
  if (isLandingHost()) return <LandingApp />;
  return <MainApp />;
};

const MainApp: React.FC = () => (
  <AuthProvider>
    <ToastProvider>
      <CargandoGlobal />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          {rutasPublicas}
          {/* Vista previa local de la landing (en producción vive en el apex) */}
          <Route path="/landing" element={<Landing />} />

          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/canciones" replace />} />
            <Route path="canciones" element={lazy(<Songs />)} />
            <Route path="canciones/nueva" element={lazy(<SongEdit />)} />
            <Route path="canciones/:id" element={lazy(<SongDetail />)} />
            <Route path="canciones/:id/editar" element={lazy(<SongEdit />)} />
            <Route path="listas" element={lazy(<Setlists />)} />
            <Route path="listas/nueva" element={lazy(<SetlistEdit />)} />
            <Route path="listas/:id" element={lazy(<SetlistDetail />)} />
            <Route path="listas/:id/editar" element={lazy(<SetlistEdit />)} />
            <Route path="mi-cuenta" element={lazy(<MyAccount />)} />
            <Route path="categorias" element={lazy(<Categorias />)} />
            <Route path="compartir" element={lazy(<Compartir />)} />
            <Route path="usuarios" element={lazy(<Users />)} />
            <Route path="super-admin" element={lazy(<SuperAdmin />)} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ToastProvider>
  </AuthProvider>
);

export default App;
